import PropTypes from "prop-types";
import Icon from "@mui/material/Icon";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import { useTranslate } from "utils/i18n";

function rateChipClass(rate) {
  if (rate >= 75) return "map-side-rate map-side-rate--good";
  if (rate >= 40) return "map-side-rate map-side-rate--mid";
  return "map-side-rate map-side-rate--low";
}

export default function MapSidePanel({
  open,
  onToggle,
  startDate,
  endDate,
  daysPresent,
  onStartDateChange,
  onEndDateChange,
  onDaysChange,
  onLoadSummary,
  loadingSummary,
  summaryRows,
  onExport,
  exportDisabled,
  radiusBuckets,
  checkInRate,
}) {
  const { t } = useTranslate();

  const assigned = summaryRows.reduce((s, r) => s + Number(r?.teachersAssigned ?? 0), 0);
  const checked = summaryRows.reduce((s, r) => s + Number(r?.teachersChecked ?? 0), 0);
  const rate = assigned > 0 ? Math.round((checked / assigned) * 100) : checkInRate ?? 0;

  const grouped = summaryRows.reduce((acc, row) => {
    const key = row?.provinceName ?? "—";
    if (!acc[key]) acc[key] = { provinceName: key, assigned: 0, checked: 0, rows: [] };
    acc[key].assigned += Number(row?.teachersAssigned ?? 0);
    acc[key].checked += Number(row?.teachersChecked ?? 0);
    acc[key].rows.push(row);
    return acc;
  }, {});

  const provinces = Object.values(grouped).sort((a, b) =>
    String(a.provinceName).localeCompare(String(b.provinceName))
  );

  return (
    <>
      <button type="button" className="map-side-toggle" onClick={onToggle} aria-expanded={open}>
        <Icon sx={{ fontSize: "1.1rem !important" }}>{open ? "chevron_right" : "analytics"}</Icon>
        {open ? t("map.panel.hide", "Hide panel") : t("map.panel.show", "Regional summary")}
      </button>

      <aside className={`map-side-panel${open ? " map-side-panel--open" : ""}`} aria-hidden={!open}>
        <SoftBox className="map-side-panel-inner">
          <SoftTypography variant="h6" fontWeight="bold" sx={{ mb: 0.5 }}>
            {t("map.panel.title", "Regional attendance")}
          </SoftTypography>
          <SoftTypography variant="caption" sx={{ color: "#64748b", display: "block", mb: 2 }}>
            {t(
              "map.panel.hint",
              "Compare assigned vs checked-in teachers for the selected zone and period."
            )}
          </SoftTypography>

          <div className="map-side-stats">
            <div className="map-side-stat">
              <span className="map-side-stat-label">{t("map.kpi.in_radius", "In radius")}</span>
              <strong>{radiusBuckets.inRadius}</strong>
            </div>
            <div className="map-side-stat">
              <span className="map-side-stat-label">{t("map.kpi.out_radius", "Out of radius")}</span>
              <strong>{radiusBuckets.outRadius}</strong>
            </div>
            <div className="map-side-stat">
              <span className="map-side-stat-label">{t("map.kpi.check_in_rate", "Check-in rate")}</span>
              <strong className={rateChipClass(rate)}>{rate}%</strong>
            </div>
          </div>

          <SoftBox component="form" onSubmit={onLoadSummary} sx={{ mb: 2 }}>
            <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
              {t("area_attendance.start_date", "Start date")}
            </SoftTypography>
            <SoftInput type="date" value={startDate} onChange={(e) => onStartDateChange(e.target.value)} sx={{ mb: 1 }} />
            <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
              {t("area_attendance.end_date", "End date")}
            </SoftTypography>
            <SoftInput type="date" value={endDate} onChange={(e) => onEndDateChange(e.target.value)} sx={{ mb: 1 }} />
            <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
              {t("area_attendance.days_present", "Min. days present")}
            </SoftTypography>
            <SoftInput
              type="number"
              min={1}
              value={daysPresent}
              onChange={(e) => onDaysChange(e.target.value)}
              sx={{ mb: 1.5 }}
            />
            <SoftButton type="submit" variant="gradient" color="info" fullWidth disabled={loadingSummary} sx={{ mb: 1 }}>
              {loadingSummary ? t("map.panel.loading", "Loading…") : t("area_attendance.search", "Load summary")}
            </SoftButton>
            <SoftButton
              type="button"
              variant="outlined"
              color="dark"
              fullWidth
              disabled={exportDisabled}
              onClick={onExport}
            >
              <Icon sx={{ mr: 0.5, fontSize: "1rem !important" }}>download</Icon>
              {t("map.export", "Export filtered teachers")}
            </SoftButton>
          </SoftBox>

          {summaryRows.length > 0 && (
            <div className="map-side-table-wrap">
              <table className="map-side-table">
                <thead>
                  <tr>
                    <th>{t("map.panel.province", "Province")}</th>
                    <th>{t("map.panel.assigned", "Assigned")}</th>
                    <th>{t("map.panel.checked", "Checked")}</th>
                  </tr>
                </thead>
                <tbody>
                  {provinces.map((group) => (
                    <tr key={group.provinceName}>
                      <td>{group.provinceName}</td>
                      <td>{group.assigned}</td>
                      <td>{group.checked}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SoftBox>
      </aside>
    </>
  );
}

MapSidePanel.propTypes = {
  open: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
  daysPresent: PropTypes.string.isRequired,
  onStartDateChange: PropTypes.func.isRequired,
  onEndDateChange: PropTypes.func.isRequired,
  onDaysChange: PropTypes.func.isRequired,
  onLoadSummary: PropTypes.func.isRequired,
  loadingSummary: PropTypes.bool.isRequired,
  summaryRows: PropTypes.arrayOf(PropTypes.object).isRequired,
  onExport: PropTypes.func.isRequired,
  exportDisabled: PropTypes.bool.isRequired,
  radiusBuckets: PropTypes.shape({
    inRadius: PropTypes.number,
    outRadius: PropTypes.number,
    unknown: PropTypes.number,
  }).isRequired,
  checkInRate: PropTypes.number,
};

MapSidePanel.defaultProps = {
  checkInRate: 0,
};
